import { AsyncPipe } from "@angular/common";
import { roleAtLeast$ } from "../../../../global-states/session/role-at-least";
import { Observable } from "rxjs";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";

import { CardModule } from "primeng/card";
import { ButtonModule } from "primeng/button";
import { TagModule } from "primeng/tag";
import {
  confirmationModalServiceInjectionToken,
  ESeverity,
  IConfirmationModalService,
  IconComponent,
  IToastService,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { IPaymentMethod } from "../../../../models/i-payment-method";
import { IBillingRestClient } from "../../../../rest-clients/billing/i-billing-rest.client";
import { billingRestClientInjectionToken } from "../../../../rest-clients/billing/billing-rest-client-injection-token";
import { ISessionStateService } from "../../../../global-states/session/i-session-state.service";
import { sessionStateServiceInjectionToken } from "../../../../global-states/session/session-state-service-injection-token";
import { ISubscription } from "../../../../models/i-subscription";
import { ICompany } from "../../../../models/i-company";
import { Subscription } from "rxjs";
import { IAddPaymentMethodModalService } from "../../../../modals/add-payment-method-modal/i-add-payment-method-modal.service";
import {
  addPaymentMethodModalServiceInjectionToken
} from "../../../../modals/add-payment-method-modal/add-payment-method-modal-service-injection-token";
import { TooltipModule } from "primeng/tooltip";
import { InputTextModule } from "primeng/inputtext";
import { FormsModule } from "@angular/forms";
import { IBillingCustomer } from "../../../../models/i-billing-customer";

@Component({
  selector: "app-payment-options-tab",
  templateUrl: "./payment-options-tab.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    CardModule,
    ButtonModule,
    TagModule,
    IconComponent,
    TooltipModule,
    InputTextModule,
    FormsModule
]
})
export class PaymentOptionsTabComponent implements OnInit, OnDestroy {
  /** Hides what the backend would refuse for the user's company role. */
  public readonly isAdmin$: Observable<boolean>;
  public paymentMethods: IPaymentMethod[] = [];
  public subscriptions: ISubscription[] = [];
  public isLoading: boolean = true;
  /** Where invoices go; admins change it. */
  public billingEmail: string = "";
  public savedBillingEmail: string | null = null;
  public isSavingBillingEmail: boolean = false;

  private _companyId: string | null = null;
  private readonly _subscriptions: Subscription = new Subscription();

  constructor(
    @Inject(billingRestClientInjectionToken) private readonly _billingRestClient: IBillingRestClient,
    @Inject(sessionStateServiceInjectionToken) private readonly _sessionStateService: ISessionStateService,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    @Inject(confirmationModalServiceInjectionToken) private readonly _confirmationModalService: IConfirmationModalService,
    @Inject(addPaymentMethodModalServiceInjectionToken) private readonly _addPaymentMethodModalService: IAddPaymentMethodModalService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this.isAdmin$ = roleAtLeast$(this._sessionStateService, "ADMIN");
  }

  ngOnInit(): void {
    this._subscriptions.add(
      this._sessionStateService.activeCompany$.subscribe((company: ICompany | null) => {
        if (company) {

          this._companyId = company.id;
          this._loadData(company.id);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public async addPaymentMethod(): Promise<void> {
    if (!this._companyId) return;

    const companyId: string = this._companyId;
    const added = await this._addPaymentMethodModalService.open(companyId);

    if (added) {
      this._loadData(companyId);
    }
  }

  public deletePaymentMethod(method: IPaymentMethod): void {
    if (!this._companyId) return;

    const companyId: string = this._companyId;
    this._confirmationModalService.open({
      title: "Zahlungsmethode entfernen",
      description: `Möchtest du die Karte •••• ${method.cardLastFour} wirklich entfernen?`,
      confirmationText: "Entfernen",
      onConfirm: () =>
        this._billingRestClient.detachPaymentMethod(companyId, method.stripePaymentMethodId).then(() => {
          this._toastService.setMessage({
            title: "Erfolgreich",
            detail: "Zahlungsmethode wurde entfernt",
            severity: ESeverity.success
          });
          this._loadData(companyId);
        }).catch(() => {
          this._toastService.setMessage({
            title: "Fehler",
            detail: "Zahlungsmethode konnte nicht entfernt werden",
            severity: ESeverity.error
          });
        })
    });
  }

  public setDefaultPaymentMethod(method: IPaymentMethod): void {
    if (!this._companyId) return;

    const companyId: string = this._companyId;
    this._billingRestClient.setDefaultPaymentMethod(companyId, method.stripePaymentMethodId).then(() => {
      this._toastService.setMessage({
        title: "Erfolgreich",
        detail: "Standard-Zahlungsmethode wurde aktualisiert",
        severity: ESeverity.success
      });
      this._loadData(companyId);
    }).catch(() => {
      this._toastService.setMessage({
        title: "Fehler",
        detail: "Standard-Zahlungsmethode konnte nicht gesetzt werden",
        severity: ESeverity.error
      });
    });
  }

  public isBillingEmailValid(): boolean {
    const email: string = this.billingEmail.trim();
    return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  public async saveBillingEmail(): Promise<void> {
    if (!this._companyId || !this.isBillingEmailValid()) return;

    this.isSavingBillingEmail = true;
    this._cdr.markForCheck();
    try {
      const customer: IBillingCustomer = await this._billingRestClient.updateBillingEmail(this._companyId, this.billingEmail.trim());
      this.savedBillingEmail = customer.email;
      this.billingEmail = customer.email ?? "";
      this._toastService.setMessage({
        title: "Erfolgreich",
        detail: "Rechnungen gehen ab jetzt an diese E-Mail-Adresse",
        severity: ESeverity.success
      });
    } catch {
      this._toastService.setMessage({
        title: "Fehler",
        detail: "Die Rechnungs-E-Mail konnte nicht geändert werden",
        severity: ESeverity.error
      });
    } finally {
      this.isSavingBillingEmail = false;
      this._cdr.markForCheck();
    }
  }

  public isCancelling: boolean = false;

  public getActiveSubscription(): ISubscription | null {
    return this.subscriptions.find(s => s.status === "ACTIVE" || s.status === "TRIALING") ?? null;
  }

  public cancelSubscription(): void {
    const subscription = this.getActiveSubscription();
    if (!this._companyId || !subscription) return;

    const companyId: string = this._companyId;
    this._confirmationModalService.open({
      title: "Abonnement kündigen",
      description: "Möchtest du dein Abonnement wirklich kündigen? Das Abonnement bleibt bis zum Ende des aktuellen Abrechnungszeitraums aktiv. Danach wirst du automatisch auf das kostenlose Starter-Paket zurückgestuft.",
      confirmationText: "Kündigen",
      onConfirm: async () => {
        this.isCancelling = true;
        this._cdr.markForCheck();

        try {
          await this._billingRestClient.cancelSubscription(companyId, {
            stripeSubscriptionId: subscription.stripeSubscriptionId,
            cancelAtPeriodEnd: true
          });

          this._toastService.setMessage({
            title: "Erfolgreich",
            detail: "Abonnement wird zum Ende des Abrechnungszeitraums gekündigt",
            severity: ESeverity.success
          });

          this._loadData(companyId);
        } catch {
          this._toastService.setMessage({
            title: "Fehler",
            detail: "Abonnement konnte nicht gekündigt werden",
            severity: ESeverity.error
          });
        } finally {
          this.isCancelling = false;
          this._cdr.markForCheck();
        }
      }
    });
  }

  public formatDate(epochSeconds: number): string {
    return new Date(epochSeconds * 1000).toLocaleDateString("de-DE");
  }

  public formatCurrency(amountCents: number, currency: string): string {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amountCents / 100);
  }

  private _loadData(companyId: string): void {
    this.isLoading = true;
    this._cdr.markForCheck();

    Promise.all([
      this._billingRestClient.listPaymentMethods(companyId).catch(() => []),
      this._billingRestClient.getSubscriptions(companyId).catch(() => []),
      this._billingRestClient.getBillingCustomer(companyId).catch(() => null)
    ]).then(([paymentMethods, subscriptions, customer]: [IPaymentMethod[], ISubscription[], IBillingCustomer | null]) => {
      this.paymentMethods = paymentMethods;
      this.subscriptions = subscriptions;
      this.savedBillingEmail = customer?.email ?? null;
      this.billingEmail = customer?.email ?? "";
      this.isLoading = false;
      this._cdr.markForCheck();
    });
  }
}
